'use client';

import { MailOpen, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';

import { loginAction, verifyAction } from '@/app/login/actions';
import { caseHandoffAction } from '@/app/auth/case/actions';
import type { CaseHandoffState } from '@/lib/operations/cases/case_handoff_operation';
import type { LoginNavigation, OtpState } from '@/lib/operations/auth/otp_operation';
import type { ActionResult } from '@/lib/utils/types';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from '@/components/ui/input-otp';

interface LoginFormProps {
  returnTo?: string;
  mode?: 'login' | 'case';
  initialError?: string;
}

interface LoginCommand {
  command: 'send' | 'verify' | 'continue' | 'resend' | 'switch';
  formData?: FormData;
}

type LoginState = CaseHandoffState | LoginNavigation | OtpState;

export function LoginForm({ returnTo, mode = 'login', initialError }: LoginFormProps) {
  const router = useRouter();
  const started = useRef(false);
  const [state, setState] = useState<LoginState>();
  const [code, setCode] = useState('');
  const [error, setError] = useState(initialError ?? '');
  const t = useTranslations('LoginForm');
  const showOtp = state?.kind === 'otp';
  const email = state && 'email' in state ? state.email : '';
  const { mutate, isPending } = useMutation({
    mutationFn: async ({ command, formData }: LoginCommand): Promise<ActionResult<LoginState>> => {
      if (mode === 'case') {
        return caseHandoffAction(command === 'send' ? 'continue' : command, formData?.get('otp')?.toString() ?? code);
      }
      const data = formData ?? new FormData();
      if (!formData) data.set('email', email);
      if (returnTo) data.set('return_to', returnTo);

      return command === 'verify' ? verifyAction(data) : loginAction(data);
    },
    onSuccess: (result) => {
      if (!result.success) {
        setError(result.error);
        return;
      }
      setError('');
      setState(result.data);
      if (result.data.kind === 'otp') setCode('');
      if (result.data.kind === 'navigate') {
        if ('external' in result.data && result.data.external) window.location.assign(result.data.href);
        else router.replace(result.data.href);
      }
    },
    onError: () => setError('genericError'),
  });

  useEffect(() => {
    if (mode !== 'case' || initialError || started.current) return;
    // Defer until effect setup settles. Strict Mode's setup/cleanup replay can
    // otherwise detach the mutation observer while the automatic send is pending.
    const timer = setTimeout(() => {
      started.current = true;
      mutate({ command: 'continue' });
    }, 0);

    return () => clearTimeout(timer);
  }, [mode, initialError, mutate]);

  return (
    <div className="w-full">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{showOtp ? t('otpTitle') : t('loginTitle')}</CardTitle>
          <CardDescription>{showOtp ? t('otpDescription', { email }) : t('loginDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <div role="alert" className="mb-4 text-sm text-destructive">
              {t.has(error) ? t(error) : error}
            </div>
          )}
          {state?.kind === 'mismatch' ? (
            <div className="space-y-6">
              <p>{t('accountMismatch', { currentEmail: state.currentEmail, email: state.email })}</p>
              <Button className="w-full" disabled={isPending} onClick={() => mutate({ command: 'switch' })}>
                {t('switchAccount')}
              </Button>
            </div>
          ) : mode === 'case' && !showOtp ? (
            initialError ? null : error ? (
              <Button className="w-full" disabled={isPending} onClick={() => mutate({ command: 'continue' })}>
                {t('retry')}
              </Button>
            ) : (
              <p role="status">{t('processing')}</p>
            )
          ) : (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (!showOtp && !isPending) {
                  mutate({ command: 'send', formData: new FormData(event.currentTarget) });
                }
              }}
            >
              <div className="flex flex-col gap-6">
                {!showOtp ? (
                  <div className="grid gap-2 transition-all duration-300">
                    <Label htmlFor="email">{t('emailLabel')}</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder={t('emailPlaceholder')}
                      required
                      autoFocus
                    />
                  </div>
                ) : (
                  <div className="transition-all duration-300">
                    <input type="hidden" name="email" value={email} />
                    {returnTo && <input type="hidden" name="return_to" value={returnTo} />}
                    <div className="grid gap-2">
                      <Label htmlFor="otp">{t('otpLabel')}</Label>
                      <div className="flex justify-center mt-2">
                        <InputOTP
                          id="otp"
                          maxLength={6}
                          name="otp"
                          value={code}
                          onChange={setCode}
                          onComplete={(completedCode: string) => {
                            if (isPending) return;
                            const formData = new FormData();
                            formData.set('email', email);
                            formData.set('otp', completedCode);
                            mutate({ command: 'verify', formData });
                          }}
                          required
                          disabled={isPending}
                          autoFocus
                        >
                          <InputOTPGroup>
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                            <InputOTPSlot index={2} />
                          </InputOTPGroup>
                          <InputOTPSeparator />
                          <InputOTPGroup>
                            <InputOTPSlot index={3} />
                            <InputOTPSlot index={4} />
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      </div>
                    </div>
                  </div>
                )}
                {!showOtp && (
                  <Button type="submit" className="w-full" disabled={isPending}>
                    {isPending ? <Loader2 className="animate-spin" /> : <MailOpen />}
                    {isPending ? t('processing') : t('loginButton')}
                  </Button>
                )}
                {showOtp && isPending && (
                  <p role="status" className="text-sm text-muted-foreground text-center">
                    {t('processing')}
                  </p>
                )}
                {showOtp && (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isPending}
                    onClick={() => mutate({ command: 'resend' })}
                  >
                    {t('resendButton')}
                  </Button>
                )}
              </div>
            </form>
          )}
        </CardContent>
      </Card>
      <div className="text-sm text-muted-foreground mt-4 text-center px-6">{t('termsText')}</div>
    </div>
  );
}
